/**
 * The caller's own presentation preferences: the ONE owner of
 * `/api/v1/me/ui-preferences` in the SPA (SPEC-FRONTEND RULE-077,
 * QUERY-KEY-GATE). Every reader goes through {@link uiPreferencesQueryOptions}
 * or {@link useUIPreferences}; every writer through {@link putUIPreferences} or
 * {@link useSetAdvancedMode}. No second fetcher may declare the key.
 *
 * PRESENTATION ONLY (SPEC-TENANT-SURFACES RULE-011, API-002/API-003). The
 * `advanced_mode` preference may HIDE the Advanced categories for this user;
 * it never makes a surface usable and is never sent to, or read by, the
 * surfaces resolver or the refusal gate. It is therefore never an input to
 * `useSurfaceSet()`; it is read only by the placement layer
 * (components/shared/AdvancedGroup) and the user's own toggle page.
 *
 * DEFAULT BY ROLE (D-TSG-22, the server's): no row -> `advanced_mode` true for
 * a tenant or platform admin, false otherwise; the client never guesses it. A
 * read that is loading, disabled or fails (a 503 from an unreachable store, a
 * network failure, anything) renders as FALSE - the categories stay hidden -
 * and {@link UIPreferencesState.unavailable}
 * lets the toggle page SAY so (API-002 503 row). The fallback is for rendering
 * only and is never written back.
 *
 * `advanced_mode_set` is read-only and derived by the server: true once the
 * user has a row. It is the "never decided" signal of the one-time hint
 * (D-TSG-07 item 3); dismissing the hint is an explicit PUT of
 * `advanced_mode: false`. While it is unknown (loading, or the read failed)
 * the state reports `advancedModeSet: null`, so a hint is never shown on a
 * guess. The page presence predicate (surfaces/usePagePresence) reads it too:
 * a whole advanced page follows the same switch (D-TSG-23 point 3).
 *
 * @module api/uiPreferences
 * @see SPEC-TENANT-SURFACES-API-002 / API-003 / RULE-011 / UI-003
 * @see TASK-TSURF-W6-ADVANCED-GROUP-CHG-004 (shared with TASK-TSURF-W6-MY-SETTINGS-PAGE)
 */
import { queryOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import client from "./client";

// ---------------------------------------------------------------------------
// API-002 / API-003 shapes (mirrored, nothing added)
// ---------------------------------------------------------------------------

/** The body of `GET /api/v1/me/ui-preferences` (API-002) and of a successful PUT (API-003). */
export interface UIPreferences {
  /** The stored preference; false when the user has no row (D-TSG-05). */
  advanced_mode: boolean;
  /** Read-only, derived: true exactly when the user has a row (D-TSG-07 item 3). */
  advanced_mode_set: boolean;
}

/** The body of `PUT /api/v1/me/ui-preferences` (API-003): exactly this key, nothing else. */
export interface UIPreferencesWrite {
  advanced_mode: boolean;
}

/** A body that is not the API-002 shape. Thrown, never turned into a default. */
export class MalformedUIPreferencesError extends Error {
  constructor(detail: string) {
    super(`/me/ui-preferences returned a body that is not the API-002 shape: ${detail}`);
    this.name = "MalformedUIPreferencesError";
  }
}

function parseUIPreferences(data: unknown): UIPreferences {
  if (typeof data !== "object" || data === null || Array.isArray(data)) {
    throw new MalformedUIPreferencesError("body is not an object");
  }
  const body = data as Record<string, unknown>;
  if (typeof body.advanced_mode !== "boolean" || typeof body.advanced_mode_set !== "boolean") {
    throw new MalformedUIPreferencesError("`advanced_mode` / `advanced_mode_set` are not booleans");
  }
  return { advanced_mode: body.advanced_mode, advanced_mode_set: body.advanced_mode_set };
}

// ---------------------------------------------------------------------------
// Query key, fetcher, writer, options
// ---------------------------------------------------------------------------

/** The ONE cache key of the caller's presentation preferences. */
export function uiPreferencesQueryKey() {
  return ["me", "ui-preferences"] as const;
}

/** GET /api/v1/me/ui-preferences (API-002) through the shared client. */
export async function fetchUIPreferences(): Promise<UIPreferences> {
  const { data } = await client.get<unknown>("/me/ui-preferences");
  return parseUIPreferences(data);
}

/**
 * PUT /api/v1/me/ui-preferences (API-003). Sends EXACTLY `{ advanced_mode }` -
 * the endpoint's key set is closed and it answers 400 to anything else.
 * Returns the stored preferences after the write.
 */
export async function putUIPreferences(write: UIPreferencesWrite): Promise<UIPreferences> {
  const body: UIPreferencesWrite = { advanced_mode: write.advanced_mode };
  const { data } = await client.put<unknown>("/me/ui-preferences", body);
  return parseUIPreferences(data);
}

/** No retry on 401 / 403 / a malformed body; one retry otherwise (a 503 or a network blip). */
function shouldRetryUIPreferences(failureCount: number, error: unknown): boolean {
  const status = isAxiosError(error) ? error.response?.status : undefined;
  if (status === 401 || status === 403) return false;
  if (error instanceof MalformedUIPreferencesError) return false;
  return failureCount < 1;
}

/**
 * The only query options for the preference. Use them for prefetch /
 * ensureQueryData as well; never declare a new queryFn for this key.
 * `staleTime` 30 s: another device's write shows on the next load or focus
 * (UI-003 interaction row "on every device after the next load").
 */
export function uiPreferencesQueryOptions() {
  return queryOptions({
    queryKey: uiPreferencesQueryKey(),
    queryFn: fetchUIPreferences,
    staleTime: 30_000,
    retry: shouldRetryUIPreferences,
  });
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

/** What a renderer needs from the preference; see the module comment for the fallbacks. */
export interface UIPreferencesState {
  /** Where the read stands. `error` renders as the default (false). */
  status: "loading" | "success" | "error";
  /**
   * The preference to RENDER with: the stored value on success, and FALSE while
   * loading, on any error (API-002 503 row, D-TSG-05) and while the read is disabled.
   */
  advancedMode: boolean;
  /** The server's `advanced_mode_set` on success; null while unknown (never guessed). */
  advancedModeSet: boolean | null;
  /** True when the read failed with 503 (the preference store is unreachable): the toggle page says so. */
  unavailable: boolean;
  /** The raw failure while `status` is `error`, else null. */
  error: unknown;
  /** Re-read the preference. */
  refetch: () => void;
}

/**
 * Read the caller's presentation preference.
 *
 * @param options.enabled - false skips the request entirely (the state then
 *   renders the default, false). The Advanced layer passes false when the tier
 *   cannot open anyway (tenant switch off, or no `ui.use_advanced`), so callers
 *   without the tier never read the preference.
 */
export function useUIPreferences(options: { enabled?: boolean } = {}): UIPreferencesState {
  const enabled = options.enabled ?? true;
  const query = useQuery({ ...uiPreferencesQueryOptions(), enabled });
  const status: UIPreferencesState["status"] =
    query.status === "success" ? "success" : query.status === "error" ? "error" : "loading";
  const data = status === "success" ? query.data : undefined;
  return {
    status,
    advancedMode: data ? data.advanced_mode : false,
    advancedModeSet: data ? data.advanced_mode_set : null,
    unavailable: status === "error" && isAxiosError(query.error) && query.error.response?.status === 503,
    error: status === "error" ? query.error : null,
    refetch: () => {
      void query.refetch();
    },
  };
}

/**
 * Write `advanced_mode` (API-003). On success the ONE cache entry is replaced
 * with the server's answer, so every Advanced category on the page appears or
 * disappears at once, with no second request. Dismissing the one-time hint is
 * `mutate(false)` (D-TSG-07 item 3). A failed write leaves the cache as it was;
 * the caller renders the error.
 */
export function useSetAdvancedMode() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (advancedMode: boolean) => putUIPreferences({ advanced_mode: advancedMode }),
    onSuccess: (stored) => {
      queryClient.setQueryData(uiPreferencesQueryKey(), stored);
    },
  });
}
